/**
 * Lista suspensa com busca (item 38): o mesmo Select de sempre (as mesmas <option> e <optgroup>, o mesmo onChange com
 * e.target.value), mas, com muitas opções, o campo aceita digitar parte do nome (projeto, pessoa, cliente, OPP…) e a
 * lista vai mostrando só o que bate. Várias palavras valem em qualquer ordem, sem diferença de acento e maiúsculas.
 * Teclado: setas, Enter e Esc. O Select (components/ui) decide quando usar: com mais de SEARCH_FROM opções.
 */
import { Children, Fragment, isValidElement, useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type MouseEventHandler, type ReactNode } from 'react';
import clsx from 'clsx';

/** A partir de quantas opções (sem contar a vazia, como "Todos") a lista ganha a busca. */
export const SEARCH_FROM = 7;

export interface ComboOption { value: string; label: string; group: string | null; disabled: boolean }

const textOf = (n: ReactNode): string => {
  if (n == null || typeof n === 'boolean') return '';
  if (typeof n === 'string' || typeof n === 'number') return String(n);
  if (Array.isArray(n)) return n.map(textOf).join('');
  if (isValidElement(n)) return textOf((n.props as { children?: ReactNode }).children);
  return '';
};

/** As <option> (e as de dentro dos <optgroup>) como lista: valor, texto, grupo e se está desligada. */
export function readOptions(children: ReactNode, group: string | null = null, out: ComboOption[] = []): ComboOption[] {
  Children.forEach(children, (ch) => {
    if (!isValidElement(ch)) return;
    const p = ch.props as { value?: string | number | readonly string[]; label?: string; disabled?: boolean; children?: ReactNode };
    if (ch.type === 'option') {
      const label = textOf(p.children).replace(/\s+/g, ' ').trim();
      out.push({ value: p.value != null ? String(p.value) : label, label, group, disabled: !!p.disabled });
    } else if (ch.type === 'optgroup') readOptions(p.children, p.label ?? null, out);
    else if (ch.type === Fragment) readOptions(p.children, group, out);
  });
  return out;
}

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Classes de largura e de posição na grade ficam na caixa de fora; as do campo (altura, texto, destaque), no campo. */
const OUTER = /^(?:[a-z0-9]+:)*(?:w-|min-w-|max-w-|flex-|basis-|grow|shrink|col-span|self-|order-|mr-auto|ml-auto)/;

export interface ComboSelectProps {
  items: ComboOption[];
  value?: string | number | readonly string[];
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean; required?: boolean; id?: string; name?: string; className?: string; title?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  'aria-label'?: string; 'aria-invalid'?: boolean | 'true' | 'false';
}

export function ComboSelect({ items, value, onChange, disabled, required, id, className, title, onClick, ...aria }: ComboSelectProps) {
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const current = items.find((o) => o.value === String(value ?? '')) ?? null;
  const blank = items.find((o) => o.value === '') ?? null;
  const shown = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    return words.length ? items.filter((o) => { const t = norm(`${o.label} ${o.group ?? ''}`); return words.every((w) => t.includes(w)); }) : items;
  }, [items, q]);
  const pickable = shown.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
  const classes = (className ?? '').split(/\s+/).filter(Boolean);
  const outer = classes.filter((c) => OUTER.test(c));
  const inner = classes.filter((c) => !OUTER.test(c));

  useEffect(() => { listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' }); }, [active, open]);

  const close = () => { setOpen(false); setQ(''); };
  const pick = (o: ComboOption) => {
    if (o.disabled) return;
    close();
    if (o.value !== String(value ?? '')) onChange?.({ target: { value: o.value, name: '' }, currentTarget: { value: o.value } } as unknown as ChangeEvent<HTMLSelectElement>);
  };
  const openAt = () => { setOpen(true); setQ(''); const i = items.findIndex((o) => o.value === String(value ?? '')); setActive(i >= 0 ? i : pickable[0] ?? 0); };
  const move = (dir: 1 | -1) => {
    if (!pickable.length) return;
    const at = pickable.indexOf(active);
    setActive(pickable[Math.min(pickable.length - 1, Math.max(0, at < 0 ? 0 : at + dir))]!);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) openAt(); else move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (!open) openAt(); else move(-1); }
    else if (e.key === 'Enter' && open) { e.preventDefault(); const o = shown[active]; if (o) pick(o); }
    else if (e.key === 'Escape' && open) { e.preventDefault(); close(); }
  };

  let lastGroup: string | null = null;
  return (
    <div className={clsx('relative', outer.length ? outer : 'w-full')} onClick={onClick} title={title}>
      <input id={id} role="combobox" aria-expanded={open} aria-controls={`${listId}-l`} aria-autocomplete="list" aria-label={aria['aria-label']} aria-invalid={aria['aria-invalid']}
        aria-activedescendant={open && shown[active] ? `${listId}-${active}` : undefined} disabled={disabled} required={required} autoComplete="off" spellCheck={false}
        className={clsx('h-9 w-full rounded-md border border-line bg-panel pl-2 pr-7 text-sm text-fg placeholder:text-fg disabled:opacity-60', open && 'placeholder:text-muted', inner)}
        // Fechado, mostra a opção escolhida; aberto, o que se digita (a escolhida fica de dica). A vazia ("Todos") vira dica.
        value={open ? q : current && current.value !== '' ? current.label : ''}
        placeholder={open ? (current?.label || 'Digite para filtrar…') : blank?.label ?? ''}
        onFocus={openAt} onBlur={close} onKeyDown={onKeyDown}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }} />
      <svg aria-hidden viewBox="0 0 24 24" className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        {open ? <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></> : <path d="m6 9 6 6 6-6" />}
      </svg>
      {open ? (
        <ul ref={listRef} id={`${listId}-l`} role="listbox" className="absolute left-0 top-full z-40 mt-1 max-h-72 w-max min-w-full max-w-[min(40rem,calc(100vw-2rem))] overflow-auto rounded-md border border-line bg-panel py-1 text-sm shadow-lg">
          {shown.length === 0 ? <li className="px-3 py-2 text-muted">Nada encontrado com "{q}".</li> : null}
          {shown.map((o, i) => {
            const header = o.group !== lastGroup && o.group ? o.group : null;
            lastGroup = o.group;
            return (
              <Fragment key={`${o.group ?? ''}|${o.value}|${i}`}>
                {header ? <li role="presentation" className="px-3 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted">{header}</li> : null}
                <li id={`${listId}-${i}`} role="option" aria-selected={o.value === String(value ?? '')} aria-disabled={o.disabled || undefined} data-active={i === active}
                  className={clsx('cursor-pointer px-3 py-1.5', o.group && 'pl-5', o.disabled && 'cursor-not-allowed opacity-50', i === active ? 'bg-accent/15 text-fg' : 'hover:bg-panel-2',
                    o.value === String(value ?? '') && 'font-medium', o.value === '' && 'text-muted')}
                  onMouseDown={(e) => { e.preventDefault(); pick(o); }} onMouseEnter={() => !o.disabled && setActive(i)}>
                  {o.label || '—'}
                </li>
              </Fragment>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
